import PublicLayout from "@/components/layout/PublicLayout";
import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { resetPasswordWithToken } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            await resetPasswordWithToken(token, password);
            track("password_reset_completed");
            setSuccess(true);
            setTimeout(() => navigate("/auth/login"), 2500);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Could not reset your password. The link may be invalid or expired.",
            );
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <PublicLayout>
                <section className="bl-paper">
                    <div
                        className="bl-container py-16 max-w-md text-center"
                        data-testid="reset-success-page"
                    >
                        <div className="mx-auto w-14 h-14 rounded-full bg-bl-primary/10 flex items-center justify-center">
                            <CheckCircle2 className="w-7 h-7 text-bl-primary" />
                        </div>
                        <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                            Password updated.
                        </h1>
                        <p className="mt-3 text-bl-muted leading-relaxed">
                            Your password has been updated. Taking you to sign
                            in…
                        </p>
                    </div>
                </section>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div
                    className="bl-container py-16 max-w-md"
                    data-testid="reset-password-page"
                >
                    <Link
                        to="/auth/login"
                        className="inline-flex items-center gap-2 text-sm text-bl-muted hover:text-bl-primary mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to sign in
                    </Link>
                    <div className="bl-eyebrow">Reset password</div>
                    <h1 className="mt-4 font-display font-semibold text-3xl sm:text-4xl tracking-tight text-bl leading-tight">
                        Choose a new{" "}
                        <span className="font-italic-accent text-bl-primary">
                            password
                        </span>
                        .
                    </h1>
                    <p className="mt-4 text-bl-muted text-sm leading-relaxed">
                        Pick something strong — at least 8 characters. You'll
                        be signed out of any other devices after the change.
                    </p>

                    <form
                        onSubmit={handleSubmit}
                        noValidate
                        className="mt-8 bl-card p-6 md:p-7 space-y-4"
                    >
                        <div>
                            <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                New password
                            </label>
                            <input
                                required
                                type="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                minLength={8}
                                data-testid="reset-password-input"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                Confirm new password
                            </label>
                            <input
                                required
                                type="password"
                                autoComplete="new-password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                minLength={8}
                                data-testid="reset-confirm-input"
                            />
                        </div>
                        {error && (
                            <div
                                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3"
                                data-testid="reset-error-message"
                            >
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="bl-btn-primary w-full"
                            data-testid="reset-submit-btn"
                        >
                            {loading ? "Updating…" : "Update password"}
                        </button>
                    </form>
                </div>
            </section>
        </PublicLayout>
    );
}
